import { ensureAnonSession, supabase } from './supabaseClient';
import { clearAllUserData } from '../data/userDataStore';
import { isNative } from '../utils/platformUtils';

// Compte utilisateur par-dessus la session anonyme posee au boot (ensureAnonSession).
//
// Connexion sans mot de passe : un code a 6 chiffres est envoye par email, puis
// verifie dans l'app. Le code (plutot que le lien magique cliquable) marche a
// l'identique sur le web et dans l'app iOS native, sans lien profond a configurer.
// Cote Supabase, les templates "Magic Link" et "Change Email" doivent contenir
// {{ .Token }} pour envoyer le code au lieu d'une URL.
//
// Deux chemins, comme avant l'email/password :
//  - Creer un compte = updateUser({ email }) SUR la session anonyme : le user_id ne
//    change pas, donc settings/patterns/achats deja syncs restent rattaches. Le code
//    est de type 'email_change' ; le verifier fait passer l'anonyme en permanent.
//  - Se reconnecter a un compte existant = signInWithOtp(shouldCreateUser:false) :
//    remplacement de session, les donnees locales du guest sont purgees (sinon leurs
//    rows dirty partiraient au prochain push sous le user_id du compte connecte) et le
//    caller recharge l'app, dont le boot re-pull les donnees du compte. Code de type 'email'.

export async function getSessionUser() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session ? session.user : null;
}

// Un user existe toujours (la session anonyme est posee au boot), donc sa seule
// presence ne dit rien. Avoir un compte, c'est avoir un email confirme sur une
// session qui n'est plus anonyme.
export function isSignedInUser(user) {
  return !!user?.email && user?.is_anonymous !== true;
}

// Callback appele avec le user courant (ou null) a chaque changement de session.
// Retourne la fonction de desabonnement.
export function onAuthChange(callback) {
  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange((_event, session) => {
    callback(session ? session.user : null);
  });
  return () => subscription.unsubscribe();
}

// Chemin creation : lie l'email a la session anonyme et envoie un code de confirmation.
// L'anonyme n'est pas encore permanent tant que le code n'est pas verifie.
export async function sendAccountCode(email) {
  const { error } = await supabase.auth.updateUser({ email });
  if (error) throw error;
}

// Reemet le jeton apres une conversion anonyme -> compte.
//
// GoTrue ne renouvelle pas la session tout seul quand un anonyme devient
// permanent : le user_id ne bouge pas, mais le JWT deja en main garde ses claims
// d'origine (is_anonymous vrai, pas d'email) jusqu'a sa propre expiration. Le
// serveur voit alors une session anonyme la ou l'UI montre un compte, et
// /api/promo/mine ne reconnait l'invitation de personne. Constate en S3 le
// 28/08/2026, confirme dans la doc Supabase le 29/08.
//
// Ne throw jamais : le compte, lui, EST cree. Un reseau coupe dans la seconde ne
// doit pas transformer une creation reussie en erreur, le jeton se remettra a
// jour a son renouvellement.
async function refreshSessionClaims() {
  try {
    await supabase.auth.refreshSession();
  } catch (err) {
    console.warn('[auth] reemission de session apres conversion echouee:', err?.message);
  }
}

// Verifie le code de creation : l'anonyme devient permanent, meme user_id, donnees conservees.
export async function confirmAccountCode(email, token) {
  const { error } = await supabase.auth.verifyOtp({ email, token, type: 'email_change' });
  if (error) throw error;
  await refreshSessionClaims();
}

// Chemin reconnexion : envoie un code au compte existant (echoue si l'email n'a pas de compte).
export async function sendSignInCode(email) {
  const { error } = await supabase.auth.signInWithOtp({
    email,
    options: { shouldCreateUser: false },
  });
  if (error) throw error;
}

// Verifie le code de reconnexion : remplace la session par celle du compte, purge le guest local.
export async function confirmSignInCode(db, email, token) {
  const { error } = await supabase.auth.verifyOtp({ email, token, type: 'email' });
  if (error) throw error;
  await switchIdentity(db);
}

// Lien d'invitation personnel : le serveur a echange le token du mail contre un
// jeton de connexion GoTrue, il ne reste qu'a le presenter. Meme consequence
// qu'une reconnexion par code : la session devient celle du compte, les donnees
// locales de l'invite ne lui appartiennent pas et sont purgees, l'appelant
// recharge ensuite.
//
// Deux types tentes : GoTrue etiquette le jeton d'un lien magique 'magiclink',
// mais l'a longtemps accepte sous 'email', et les deux formes cohabitent selon
// la version du projet. Un seul aller-retour de plus en cas de refus, contre un
// parcours entier casse si on pariait sur le mauvais.
export async function confirmInviteToken(db, tokenHash) {
  let { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'magiclink' });
  if (error) {
    ({ error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'email' }));
  }
  if (error) throw error;
  await switchIdentity(db);
}

// Redirection de retour OAuth : le custom scheme natif (capte par useOAuthReturn via
// appUrlOpen) ou l'origine web courante. Les deux doivent figurer dans les "Redirect URLs"
// autorisees du projet Supabase.
export function oauthRedirectTo() {
  return isNative()
    ? 'upscale://auth-callback'
    : `${window.location.origin}${window.location.pathname}`;
}

// Sur le web le retour OAuth passe par un rechargement complet : on note quel chemin a
// ete pris pour savoir, au retour, s'il faut purger les donnees du guest ('signin') ou
// les garder ('link'), et pour ne pas boucler sur le repli.
const OAUTH_MODE_KEY = 'upscale_oauth_mode';

function setOAuthMode(mode) {
  try {
    localStorage.setItem(OAUTH_MODE_KEY, mode);
  } catch {
    // stockage indisponible : on retombe sur le comportement 'link'
  }
}

export function readOAuthMode() {
  try {
    return localStorage.getItem(OAUTH_MODE_KEY);
  } catch {
    return null;
  }
}

function clearOAuthMode() {
  try {
    localStorage.removeItem(OAUTH_MODE_KEY);
  } catch {
    // rien a nettoyer
  }
}

// Connexion arrivee sans geste : le lien personnel ouvert dans l'app installee,
// et demain le referrer d'installation Android. Dans ces deux cas l'app se
// recharge et revient connectee a un compte que l'utilisateur n'a pas choisi a
// l'ecran ; sans un mot au retour, il decouvre une session dont il ignore
// l'adresse. Le toast est pose par la surface, ici on ne fait que transmettre
// la marque a travers le rechargement.
//
// Datee, et lue une seule fois : un claim qui marque puis dont la session
// n'aboutit jamais ne doit pas faire annoncer une connexion au lancement
// suivant.
const SILENT_SIGNIN_KEY = 'upscale_silent_signin';
const SILENT_SIGNIN_MAX_AGE_MS = 5 * 60 * 1000;

export function markSilentSignIn() {
  try {
    localStorage.setItem(SILENT_SIGNIN_KEY, String(Date.now()));
  } catch {
    // stockage indisponible : pas de toast, le parcours reste entier
  }
}

// Rend true une seule fois, et efface la marque dans tous les cas : une marque
// perimee n'a plus rien a annoncer non plus.
export function takeSilentSignIn(now = Date.now()) {
  let at = null;
  try {
    at = localStorage.getItem(SILENT_SIGNIN_KEY);
    if (at !== null) localStorage.removeItem(SILENT_SIGNIN_KEY);
  } catch {
    return false;
  }
  if (at === null) return false;
  const stamp = Number(at);
  return Number.isFinite(stamp) && now - stamp < SILENT_SIGNIN_MAX_AGE_MS;
}

// Le parcours d'achat qui traverse un rechargement.
//
// Sur le web, le verrou envoie poser un compte AVANT la caisse (F3) et reste
// ouvert derriere la feuille de compte : par code email cree, rien ne recharge,
// la feuille se retire et on retombe sur le verrou qui dit ou on en est. Google
// quitte la page, et le retour se termine par un reload complet qui emporte tout
// l'etat React : l'app se rouvre connectee sur l'ecran normal, sans verrou et
// sans trace de l'achat entame, et on en conclut que rien ne s'est passe --
// exactement le constat qui avait deja donne backFromAccountStep un cran plus
// tot. La reconnexion par code a un compte existant recharge elle aussi.
//
// On garde donc le contexte du verrou de l'autre cote du rechargement, pour
// rouvrir le meme ecran. Le contexte seulement : rouvrir la caisse toute seule
// ferait payer sans geste, payer reste un acte conscient.
//
// localStorage et pas sessionStorage : c'est ce que traverse deja OAUTH_MODE_KEY,
// et le retour natif peut relancer l'app a froid, ce qu'une session de webview ne
// survit pas. La peremption remplace la duree de vie de l'onglet.
//
// 15 minutes, la ou la connexion silencieuse se contente de 5 : le detour par
// Google fait choisir un compte, parfois saisir un mot de passe et valider un
// second facteur.
const UNLOCK_PENDING_KEY = 'upscale_unlock_pending';
const UNLOCK_PENDING_MAX_AGE_MS = 15 * 60 * 1000;

// Le contexte tel que la feuille l'attend. Relu depuis un stockage que n'importe
// quoi peut avoir ecrit : un contexte a moitie valide rouvrirait un verrou
// intitule "undefined".
function isUnlockContext(value) {
  if (!value || typeof value !== 'object') return false;
  if (value.type === 'mode') return typeof value.mode === 'string';
  if (value.type === 'root') return typeof value.note === 'string';
  // Le hub vend l'app entiere : son contexte n'a pas d'item a valider.
  if (value.type === 'hub') return true;
  return false;
}

export function markPendingUnlock(context) {
  if (!isUnlockContext(context)) return;
  try {
    localStorage.setItem(UNLOCK_PENDING_KEY, JSON.stringify({ context, at: Date.now() }));
  } catch {
    // stockage indisponible : le parcours reste entier, il perd sa memoire
  }
}

// Rend le contexte une seule fois, et efface la marque dans tous les cas : une
// marque perimee ou illisible n'a plus rien a rouvrir non plus.
export function takePendingUnlock(now = Date.now()) {
  let raw = null;
  try {
    raw = localStorage.getItem(UNLOCK_PENDING_KEY);
    if (raw !== null) localStorage.removeItem(UNLOCK_PENDING_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  let parsed = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  const at = Number(parsed?.at);
  if (!Number.isFinite(at) || now - at >= UNLOCK_PENDING_MAX_AGE_MS) return null;
  return isUnlockContext(parsed?.context) ? parsed.context : null;
}

// A appeler des que le parcours se conclut sans rechargement : verrou ferme par
// l'utilisateur, achat abouti, retour de l'etape compte par code email. Sans ca
// la marque ressusciterait un verrou fantome a un lancement ulterieur.
export function clearPendingUnlock() {
  try {
    localStorage.removeItem(UNLOCK_PENDING_KEY);
  } catch {
    // rien a nettoyer
  }
}

// Bascule d'identité : la session courante est remplacée par celle d'un compte
// existant (repli Apple/Google, ou reconnexion par code). Le miroir local du
// guest ne lui appartient plus, on le purge.
//
// Rien n'est tenté sur l'achat de l'appareil : il reste sur l'app_user_id
// précédent, et seul un Restore demandé par la personne le déplace. Le boot le
// faisait tout seul jusqu'au 11/09/2026, ce qui envoyait l'achat d'un téléphone
// vers le premier compte connecté, sans que personne l'ait demandé.
async function switchIdentity(db) {
  await clearAllUserData(db);
}

// Web : signInWithOAuth/linkIdentity redirigent la page eux-memes. Natif : on garde la
// main (skipBrowserRedirect) et on ouvre l'URL dans @capacitor/browser, le retour deep
// link etant traite par useOAuthReturn.
async function openOAuthUrl(data) {
  if (!isNative() || !data?.url) return;
  const { Browser } = await import('@capacitor/browser');
  try {
    await Browser.open({ url: data.url });
  } catch (err) {
    // iOS refuse d'ouvrir un navigateur tant que le precedent n'a pas fini de se
    // fermer : l'appel echoue avec "Unable to display URL" et le natif logue
    // "Unbalanced calls to begin/end appearance transitions". Le repli vers la
    // connexion pure (identite Google deja rattachee) tombe exactement dans cette
    // fenetre, puisque useOAuthReturn vient d'appeler Browser.close(). On laisse la
    // transition se terminer et on retente une fois. Constate le 30/07/2026 : sans
    // ca, la feuille Google s'ouvrait deux secondes et disparaissait, en boucle.
    await new Promise((resolve) => setTimeout(resolve, 700));
    await Browser.open({ url: data.url });
  }
}

// Le depart OAuth exige un jeton en main. ensureAnonSession() rend l'objet
// session memoise, mais linkIdentity et signInWithOAuth relisent la session dans
// le storage avant d'envoyer la requete : si cette lecture rend null, la requete
// part avec la seule cle anonyme et GoTrue repond "403 invalid claim: missing sub
// claim", sans que la feuille du provider s'ouvre jamais. Constate le 06/09/2026
// sur iOS Safari, page d'invitation : session anonyme posee 7 s plus tot, jamais
// relue, les trois portes en echec. On relit donc jusqu'a voir le jeton, et si le
// storage reste muet on y repose la session connue avant de partir.
const OAUTH_SESSION_READS = 3;
const OAUTH_SESSION_RETRY_MS = 150;
export const OAUTH_NO_SESSION = 'oauth_start_no_session';

async function sessionForOAuthStart() {
  const known = await ensureAnonSession();
  for (let attempt = 0; attempt < OAUTH_SESSION_READS; attempt += 1) {
    const { data } = await supabase.auth.getSession();
    if (data?.session?.access_token) return data.session;
    await new Promise((resolve) => setTimeout(resolve, OAUTH_SESSION_RETRY_MS));
  }
  if (known?.access_token && known?.refresh_token) {
    const { data } = await supabase.auth.setSession({
      access_token: known.access_token,
      refresh_token: known.refresh_token,
    });
    if (data?.session?.access_token) return data.session;
  }
  throw new Error(OAUTH_NO_SESSION);
}

// Login par redirection OAuth : lie l'identite au user courant (anonyme ou email), donc
// le user_id et les donnees restent. On passe par le flow OAuth web (linkIdentity), pas
// signInWithIdToken, qui deconnecterait l'anonyme au lieu de le lier.
// Requiert "Manual linking" active dans le projet Supabase + le provider configure.
//
// Generique par provider parce que la page d'invitation offre aussi Apple sur le web
// (App Store 4.8 : Sign in with Apple partout ou Google apparait). Cote Supabase, Apple
// web est un provider de plus a configurer, la mecanique PKCE est la meme.
export async function startProviderLink(provider) {
  // La session de base d'abord : si elle se pose pendant le depart, elle emporte
  // le code verifier PKCE avec le storage (cf. ensureAnonSession). Et son jeton
  // relu, sinon la requete part sans identite (cf. sessionForOAuthStart).
  await sessionForOAuthStart();
  setOAuthMode('link');
  const { data, error } = await supabase.auth.linkIdentity({
    provider,
    options: {
      redirectTo: oauthRedirectTo(),
      skipBrowserRedirect: isNative(),
      // Sur un navigateur deja connecte a Google, le bouton ne montre rien du
      // tout : Google reprend le compte actif et redirige dans la seconde.
      // Quelqu'un qui a deux comptes, le perso et celui du travail, ne voit
      // donc jamais lequel porte son acces -- alors que l'interstitiel vient de
      // lui poser la question, et que tout le parcours v5 tient a ce choix
      // (scenario Marc/brol, F26). On redemande donc le selecteur.
      //
      // Google seulement : Apple gere son propre ecran de choix et ne connait
      // pas ce parametre.
      ...(provider === 'google' ? { queryParams: { prompt: 'select_account' } } : {}),
    },
  });
  if (error) throw error;
  await openOAuthUrl(data);
}

// Repli quand l'email du provider a deja un compte : le rattachement est refuse au retour
// (error_code=email_exists) parce que la session courante est un autre user. On refait le
// tour en connexion pure, qui remplace la session par celle du compte existant. Le
// provider ne redemande pas le consentement, l'utilisateur ne voit qu'un aller-retour.
//
// Pas de selecteur de compte dans le repli, contrairement au rattachement : la
// personne vient de choisir, ce tour-ci doit rester invisible. Le lui redemander
// la ferait choisir deux fois pour un seul geste.
//
// Sert aussi de depart direct (chooseAccount) quand la feuille de compte est en
// mode "Sign in" : le compte existe, rattacher a la session anonyme echouerait
// avec identity_already_exists et repartirait en repli. Constate le 09/09/2026
// (Christophe) et le 06/09 (Anthony) : sur un navigateur qui porte plusieurs
// comptes Google, le repli n'est pas invisible, Google redemande le compte, donc
// deux selecteurs pour un geste. Le depart direct est un seul aller-retour, et
// c'est lui qui demande le selecteur, comme le rattachement.
export async function startProviderSignIn(provider, { chooseAccount = false } = {}) {
  // Meme attente que le rattachement, et elle compte double ici : ce repli part
  // tout seul au rechargement, sans geste humain pour laisser le boot finir.
  await sessionForOAuthStart();
  setOAuthMode('signin');
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: oauthRedirectTo(),
      skipBrowserRedirect: isNative(),
      ...(chooseAccount && provider === 'google' ? { queryParams: { prompt: 'select_account' } } : {}),
    },
  });
  if (error) throw error;
  await openOAuthUrl(data);
}

export function startGoogleLink() {
  return startProviderLink('google');
}

export function startGoogleSignIn(options) {
  return startProviderSignIn('google', options);
}

// Sign in with Apple, flux natif iOS. Rien a voir avec Google : aucun navigateur ne
// s'ouvre, aucun deep link ne revient. iOS rend directement un identity token signe par
// Apple, que Supabase verifie avec les cles publiques d'Apple et dont il controle que
// l'audience figure dans les "Client IDs" du provider. Tout se joue dans cet appel, d'ou
// l'absence de passage par useOAuthReturn, et d'ou l'inutilite d'un client secret (celui
// du flux web, qu'il faudrait regenerer tous les six mois).
//
// Nonce : Apple recopie dans le token, tel quel, celui qu'on lui passe. Supabase, lui,
// hache en SHA-256 hexadecimal le nonce qu'on lui envoie et compare au token. On donne
// donc l'empreinte a Apple et l'original a Supabase.
const APPLE_CLIENT_ID = 'com.christophenovalet.upscale';

function randomNonce() {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function sha256Hex(value) {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

// Meme situation que le repli Google : l'identite Apple appartient deja a un compte, soit
// par l'identite elle-meme, soit par l'email qu'elle porte. Supabase le dit tantot par un
// code, tantot seulement en clair dans le message, d'ou la double lecture.
function isIdentityTaken(error) {
  const code = error?.code || error?.error_code;
  if (code === 'email_exists' || code === 'identity_already_exists') return true;
  return /already (been )?(registered|linked)|already exists|identity is already/i.test(
    error?.message || ''
  );
}

// Rend 'linked' quand l'identite a rejoint la session courante (meme user_id, donc
// reglages, patterns et achats restent), 'signed-in' quand il a fallu basculer sur le
// compte existant. Dans ce second cas les donnees locales de l'invite sont purgees, comme
// pour confirmSignInCode, et l'appelant recharge l'app pour re-puller celles du compte.
export async function startAppleSignIn(db) {
  const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
  const rawNonce = randomNonce();

  const { response } = await SignInWithApple.authorize({
    clientId: APPLE_CLIENT_ID,
    // Le flux natif ne redirige nulle part ; le champ n'est lu que par le flux web.
    redirectURI: '',
    scopes: 'email name',
    nonce: await sha256Hex(rawNonce),
  });

  const token = response?.identityToken;
  if (!token) throw new Error('Apple did not return an identity token.');

  const { error } = await supabase.auth.linkIdentity({ provider: 'apple', token, nonce: rawNonce });
  if (!error) {
    await refreshSessionClaims();
    return 'linked';
  }
  if (!isIdentityTaken(error)) throw error;

  const { error: signInError } = await supabase.auth.signInWithIdToken({
    provider: 'apple',
    token,
    nonce: rawNonce,
  });
  if (signInError) throw signInError;
  await switchIdentity(db);
  return 'signed-in';
}

// Retour OAuth : echange le code PKCE (present dans l'URL de retour) contre la session.
// Apres un link, l'identite Google est rattachee au meme user_id et les donnees restent.
// Apres le repli signin, la session devient celle du compte existant : les donnees locales
// du guest ne lui appartiennent pas, meme purge que confirmSignInCode avant que le boot
// re-pull celles du compte.
export async function completeOAuthReturn(code, db) {
  const mode = readOAuthMode();
  const { error } = await supabase.auth.exchangeCodeForSession(code);
  if (error) {
    clearOAuthMode();
    throw error;
  }
  if (mode === 'signin' && db) await switchIdentity(db);
  // Rattachement seulement : le repli signin rend deja la session du compte
  // existant, elle n'a jamais ete anonyme.
  else if (mode === 'link') await refreshSessionClaims();
  clearOAuthMode();
}

export async function signOutToGuest(db) {
  await clearAllUserData(db);
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}
